import React, { useState } from 'react';
import { Heading, IconButton, Tooltip } from '@lumx/react';
import MdiIcon from '@mdi/react';
import {
  mdiArrowLeft,
  mdiChevronDown,
  mdiChevronUp,
  mdiInformationOutline,
} from '@mdi/js';
import './platform-groups.css';

interface Group {
  id: string;
  name: string;
  members: number;
}

interface Category {
  id: string;
  name: string;
  groups: Group[];
}

const CATEGORIES: Category[] = [
  {
    id: 'business',
    name: 'Business',
    groups: [
      { id: 'g1',  name: 'Marketing Group',   members: 50  },
      { id: 'g2',  name: 'Finance Group',      members: 50  },
      { id: 'g8',  name: 'Legal Compliance',   members: 25  },
      { id: 'g9',  name: 'Customer Success',   members: 120 },
      { id: 'g14', name: 'Comms & Brand',      members: 20  },
      { id: 'g18', name: 'Executive Team',     members: 12  },
    ],
  },
  {
    id: 'pe',
    name: 'P&E',
    groups: [
      { id: 'g3',  name: 'R&D Group',          members: 50  },
      { id: 'g4',  name: 'P&E Group',          members: 150 },
      { id: 'g7',  name: 'Engineering EU',     members: 80  },
      { id: 'g10', name: 'Product Design',     members: 45  },
      { id: 'g12', name: 'Data Science',       members: 35  },
      { id: 'g17', name: 'Operations APAC',    members: 90  },
    ],
  },
  {
    id: 'sales',
    name: 'Sales',
    groups: [
      { id: 'g5',  name: 'Sales EMEA',         members: 200 },
      { id: 'g16', name: 'Sales Americas',     members: 180 },
    ],
  },
  {
    id: 'hr',
    name: 'HR',
    groups: [
      { id: 'g6',  name: 'HR Global',          members: 300 },
    ],
  },
  {
    id: 'it',
    name: 'IT',
    groups: [
      { id: 'g11', name: 'IT Operations',      members: 60  },
      { id: 'g15', name: 'Security',           members: 15  },
    ],
  },
  {
    id: 'finance',
    name: 'Finance',
    groups: [
      { id: 'g13', name: 'Finance APAC',       members: 40  },
    ],
  },
];

interface PlatformGroupsPageProps {
  onBack?: () => void;
}

const PlatformGroupsPage: React.FC<PlatformGroupsPageProps> = ({ onBack }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (catId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(catId) ? next.delete(catId) : next.add(catId);
      return next;
    });
  };

  return (
    <div className="pg-page">
      <div className="pg-header">
        {onBack && (
          <IconButton label="Back" icon={mdiArrowLeft} emphasis="low" onClick={onBack} />
        )}
        <Heading as="h1" typography="headline">Platform groups</Heading>
      </div>

      <div className="pg-list">
        {CATEGORIES.map(cat => {
          const isExpanded = expanded.has(cat.id);

          return (
            <div key={cat.id} className="pg-category">
              <div className="pg-category__head">
                <Heading as="h2" typography="subtitle1">{cat.name}</Heading>
                <div style={{ flex: 1 }} />
                <button
                  className="pg-icon-btn"
                  onClick={() => toggleExpand(cat.id)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <MdiIcon path={isExpanded ? mdiChevronUp : mdiChevronDown} size={0.85} />
                </button>
              </div>

              {isExpanded && (
                <div className="pg-category__body">
                  {cat.groups.map(group => (
                    <div key={group.id} className="pg-group-row">
                      <Heading as="h2" typography="subtitle1">{group.name}</Heading>
                      <div style={{ flex: 1 }} />
                      <Tooltip label={`${group.members.toLocaleString()} members`} placement="top">
                        <button className="pg-icon-btn pg-icon-btn--muted" aria-label="Group info">
                          <MdiIcon path={mdiInformationOutline} size={0.75} />
                        </button>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformGroupsPage;
